
import React, { useEffect, useState } from "react";
import "./ScenarioPublicite.css";

export default function ScenarioPublicite({ data }) {
    const scenario = data?.scenario ?? data;
    const apiUrl = process.env.REACT_APP_API_URL;

    const [sceneActive, setSceneActive] = useState(0);
    const [detailsOuverts, setDetailsOuverts] = useState({});
    const [copie, setCopie] = useState(null);
    const [videos, setVideos] = useState({});
    const [generationEnCours, setGenerationEnCours] = useState({});
    const [erreursVideo, setErreursVideo] = useState({});
    useEffect(() => {
        const tachesEnCours = Object.entries(videos).filter(
            ([, video]) =>
                video.taskId &&
                !["SUCCEEDED", "FAILED", "CANCELLED"].includes(video.status)
        );

        if (tachesEnCours.length === 0) return;

        let annule = false;

        const timer = setTimeout(async () => {
            const resultats = await Promise.all(
                tachesEnCours.map(async ([numero, video]) => {
                    try {
                        const url = new URL(
                            `${apiUrl}/api/createVideo/verifierTache`
                        );
                        url.searchParams.set("id", video.taskId);

                        const response = await fetch(url, {
                            headers: {
                                Accept: "application/json",
                                // Authorization: `Bearer ${token}`,
                            },
                        });

                        const data = await response.json();

                        if (!response.ok || !data.success) {
                            throw new Error(
                                data.error || "Erreur de vérification."
                            );
                        }

                        return {
                            numero,
                            status: data.status,
                            videoUrl: data.video_url || null,
                            error: data.error || null,
                        };
                    } catch (error) {
                        return {
                            numero,
                            error: error.message || "Erreur de vérification.",
                        };
                    }
                })
            );

            if (annule) return;

            setVideos((prev) => {
                const next = { ...prev };

                for (const resultat of resultats) {
                    const ancienne = next[resultat.numero];
                    if (!ancienne) continue;

                    if (resultat.status) {
                        next[resultat.numero] = {
                            ...ancienne,
                            status: resultat.status,
                            videoUrl: resultat.videoUrl ?? ancienne.videoUrl,
                        };
                    }
                }

                return next;
            });

            for (const resultat of resultats) {
                if (resultat.status === "FAILED" || resultat.status === "CANCELLED") {
                    setErreursVideo((prev) => ({
                        ...prev,
                        [resultat.numero]:
                            resultat.error || "La génération vidéo a échoué.",
                    }));
                }
            }
        }, 5000);

        return () => {
            annule = true;
            clearTimeout(timer);
        };
    }, [videos, apiUrl]);

    if (data?.success === false || !scenario?.scenes?.length) {
        return (
            <div className="scenario-empty">
                Aucun scénario publicitaire disponible.
            </div>
        );
    }

    const scenes = scenario.scenes;
    const scene = scenes[sceneActive];

    const formatTemps = (secondes) => {
        const valeur = Number(secondes) || 0;
        return `00:${String(valeur).padStart(2, "0")}`;
    };

    const API_SCENE_URL =
        "https://intereseaux.com/api/videos/generer-scene";

    const genererVideoScene = async (scene, index) => {
        const numero = String(scene.numero ?? index + 1);

        setGenerationEnCours((prev) => ({ ...prev, [numero]: true }));
        setErreursVideo((prev) => ({ ...prev, [numero]: "" }));

        try {
            const response = await fetch(
                `${apiUrl}/api/createVideo/generer-scene`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        // Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ scene }),
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success || !data.task_id) {
                throw new Error(
                    data.error || "Impossible de lancer la génération vidéo."
                );
            }

            setVideos((prev) => ({
                ...prev,
                [numero]: {
                    taskId: data.task_id,
                    status: data.status || "PENDING",
                    videoUrl: null,
                },
            }));
        } catch (error) {
            setErreursVideo((prev) => ({
                ...prev,
                [numero]: error.message || "Une erreur est survenue.",
            }));
        } finally {
            setGenerationEnCours((prev) => ({
                ...prev,
                [numero]: false,
            }));
        }
    };
    const basculerDetails = (numero) => {
        setDetailsOuverts((ancien) => ({
            ...ancien,
            [numero]: !ancien[numero],
        }));
    };

    const copierTexte = async (texte, identifiant) => {
        try {
            await navigator.clipboard.writeText(texte);
            setCopie(identifiant);
            window.setTimeout(() => setCopie(null), 1800);
        } catch {
            setCopie(null);
        }
    };


    return (
        <main className="scenario">
            {/* En-tête */}
            <header className="scenario-header">
                <div className="scenario-eyebrow">
                    <span className="scenario-status-dot" />
                    SCÉNARIO PUBLICITAIRE
                </div>

                <h1>{scenario.titre}</h1>
                <p className="scenario-concept">{scenario.concept}</p>

                <div className="scenario-stats">
                    <div className="scenario-stat">
                        <span>Durée</span>
                        <strong>{scenario.duree_totale_secondes ?? 30} sec</strong>
                    </div>
                    <div className="scenario-stat">
                        <span>Scènes</span>
                        <strong>{scenes.length}</strong>
                    </div>
                    <div className="scenario-stat">
                        <span>Format</span>
                        <strong>Vertical 9:16</strong>
                    </div>
                </div>
            </header>

            {/* Informations générales */}
            <section className="scenario-section">
                <h2>Direction artistique</h2>

                <div className="scenario-art">
                    <div>
                        <span className="scenario-label">Style</span>
                        <p>{scenario.direction_artistique?.style}</p>
                    </div>

                    <div>
                        <span className="scenario-label">Ambiance</span>
                        <p>{scenario.direction_artistique?.ambiance}</p>
                    </div>

                    <div>
                        <span className="scenario-label">Musique</span>
                        <p>{scenario.direction_artistique?.musique}</p>
                    </div>

                    <div>
                        <span className="scenario-label">Palette</span>
                        <div className="scenario-colors">
                            {scenario.direction_artistique?.palette_couleurs?.map(
                                (couleur) => (
                                    <span key={couleur} className="scenario-color">
                                        {couleur}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {scenario.accroche && (
                    <div className="scenario-hook">
                        <span className="scenario-label">Accroche publicitaire</span>
                        <p>« {scenario.accroche} »</p>
                    </div>
                )}
            </section>

            {/* Timeline */}
            <section className="scenario-section">
                <div className="scenario-section-heading">
                    <h2>Storyboard</h2>
                    <span>{sceneActive + 1} / {scenes.length}</span>
                </div>

                <div className="scenario-timeline">
                    {scenes.map((s, index) => (
                        <button
                            type="button"
                            key={s.numero ?? index}
                            className={
                                sceneActive === index
                                    ? "scenario-timeline-item active"
                                    : "scenario-timeline-item"
                            }
                            onClick={() => setSceneActive(index)}
                            aria-pressed={sceneActive === index}
                        >
                            <span className="scenario-timeline-number">
                                {String(s.numero ?? index + 1).padStart(2, "0")}
                            </span>
                            <span className="scenario-timeline-name">
                                {formatTemps(s.debut_seconde)}–
                                {formatTemps(s.fin_seconde)}
                            </span>
                            <span className="scenario-timeline-bar">
                                <span
                                    style={{
                                        width: `${Math.max(
                                            0,
                                            Number(s.fin_seconde) -
                                            Number(s.debut_seconde)
                                        ) /
                                            Math.max(1, Number(scenario.duree_totale_secondes) || 30) *
                                            100
                                            }%`,
                                    }}
                                />
                            </span>
                        </button>
                    ))}
                </div>

                {/* Scène sélectionnée */}
                <article className="scenario-scene">
                    <div className="scenario-scene-top">
                        <div>
                            <span className="scenario-label">
                                SCÈNE {scene.numero ?? sceneActive + 1}
                            </span>
                            <h3>
                                {formatTemps(scene.debut_seconde)} –{" "}
                                {formatTemps(scene.fin_seconde)}
                            </h3>
                        </div>

                        <span className="scenario-duration">
                            {Math.max(
                                0,
                                Number(scene.fin_seconde) -
                                Number(scene.debut_seconde)
                            )}{" "}
                            secondes
                        </span>
                    </div>

                    <p className="scenario-description">{scene.description}</p>

                    <div className="scenario-detail-grid">
                        <div className="scenario-detail">
                            <span className="scenario-label">Visuel</span>
                            <p>{scene.visuel}</p>
                        </div>

                        <div className="scenario-detail">
                            <span className="scenario-label">Mouvement caméra</span>
                            <p>{scene.mouvement_camera}</p>
                        </div>
                    </div>

                    {scene.voix_off && (
                        <div className="scenario-voice">
                            <span className="scenario-label">🎙 Voix off</span>
                            <p>« {scene.voix_off} »</p>
                            <button
                                type="button"
                                className="scenario-copy"
                                onClick={() =>
                                    copierTexte(scene.voix_off, "voix")
                                }
                            >
                                {copie === "voix" ? "Copié !" : "Copier la voix off"}
                            </button>
                        </div>
                    )}

                    {scene.texte_ecran && (
                        <div className="scenario-screen-text">
                            <span className="scenario-label">Texte à l’écran</span>
                            <p>{scene.texte_ecran}</p>
                        </div>
                    )}

                    <div className="scenario-prompt">
                        <div className="scenario-prompt-heading">
                            <span className="scenario-label">Prompt vidéo (anglais)</span>
                            <button
                                type="button"
                                className="scenario-copy"
                                onClick={() =>
                                    copierTexte(scene.prompt_video_en ?? "", "prompt")
                                }
                            >
                                {copie === "prompt" ? "Copié !" : "Copier le prompt"}
                            </button>

                            {(() => {
                                const numero = String(scene.numero ?? sceneActive + 1);
                                const video = videos[numero];
                                const envoi = generationEnCours[numero];

                                const terminee = video?.status === "SUCCEEDED";
                                const enCours =
                                    envoi ||
                                    (video?.taskId &&
                                        !["SUCCEEDED", "FAILED", "CANCELLED"].includes(video.status));

                                return (
                                    <div className="scenario-video-actions">
                                        <button
                                            type="button"
                                            className="scenario-primary-button"
                                            disabled={Boolean(enCours)}
                                            onClick={() => genererVideoScene(scene, sceneActive)}
                                        >
                                            {envoi
                                                ? "Envoi à Runway..."
                                                : enCours
                                                    ? "Génération en cours..."
                                                    : terminee
                                                        ? "Régénérer la vidéo"
                                                        : "Générer la vidéo de cette scène"}
                                        </button>

                                        {video?.taskId && (
                                            <p className="scenario-video-status" aria-live="polite">
                                                {terminee
                                                    ? "Vidéo prête"
                                                    : video.status === "FAILED"
                                                        ? "Échec de la génération"
                                                        : video.status === "CANCELLED"
                                                            ? "Génération annulée"
                                                            : `Statut Runway : ${video.status}`}
                                            </p>
                                        )}

                                        {erreursVideo[numero] && (
                                            <p className="scenario-video-error" role="alert">
                                                {erreursVideo[numero]}
                                            </p>
                                        )}

                                        {terminee && video.videoUrl && (
                                            <div className="scenario-video-result">
                                                <h4>Vidéo générée</h4>
                                                <video
                                                    key={video.videoUrl}
                                                    controls
                                                    playsInline
                                                    preload="metadata"
                                                    src={video.videoUrl}
                                                >
                                                    Ton navigateur ne prend pas en charge la lecture vidéo.
                                                </video>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                        </div>

                        <p>{scene.prompt_video_en}</p>
                    </div>

                    {scene.transition && (
                        <div className="scenario-transition">
                            <span className="scenario-label">Transition</span>
                            <span>{scene.transition}</span>
                        </div>
                    )}

                    <div className="scenario-navigation">
                        <button
                            type="button"
                            disabled={sceneActive === 0}
                            onClick={() => setSceneActive((i) => i - 1)}
                        >
                            ← Précédente
                        </button>

                        <button
                            type="button"
                            disabled={sceneActive === scenes.length - 1}
                            onClick={() => setSceneActive((i) => i + 1)}
                        >
                            Suivante →
                        </button>
                    </div>
                </article>
            </section>

            {/* Appel à l'action */}
            <section className="scenario-section">
                <h2>Final de la publicité</h2>

                <div className="scenario-cta">
                    <span className="scenario-label">Appel à l’action</span>
                    <p>{scenario.appel_action_final}</p>
                    <button
                        type="button"
                        className="scenario-primary-button"
                        onClick={() =>
                            copierTexte(scenario.appel_action_final ?? "", "cta")
                        }
                    >
                        {copie === "cta" ? "Copié !" : "Copier l’appel à l’action"}
                    </button>
                </div>

                {scenario.legende_reseaux_sociaux && (
                    <div className="scenario-social">
                        <span className="scenario-label">Légende réseaux sociaux</span>
                        <p>{scenario.legende_reseaux_sociaux}</p>
                        <button
                            type="button"
                            className="scenario-copy"
                            onClick={() =>
                                copierTexte(
                                    [
                                        scenario.legende_reseaux_sociaux,
                                        ...(scenario.hashtags ?? []),
                                    ].join("\n\n"),
                                    "social"
                                )
                            }
                        >
                            {copie === "social" ? "Copié !" : "Copier la publication"}
                        </button>
                    </div>
                )}
            </section>
        </main>
    );
}
